import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, unwrap } from '~/lib/api'

// ['league', teamId, season, …]: the server invalidates ['league', teamId] after league imports.
export const leagueKeys = {
  table: (teamId: string, season: string) => ['league', teamId, season, 'table'] as const,
  suspensions: (teamId: string, season: string) => ['league', teamId, season, 'suspensions'] as const,
  scorers: (teamId: string, season: string) => ['league', teamId, season, 'scorers'] as const,
  clubs: (teamId: string, season: string) => ['league', teamId, season, 'clubs'] as const,
  club: (teamId: string, season: string, id: string) => ['league', teamId, season, 'club', id] as const,
  person: (personId: string, season: string) => ['league', 'person', personId, season, 'suspensions'] as const,
}

export const tableQuery = (teamId: string, season: string) =>
  queryOptions({
    queryKey: leagueKeys.table(teamId, season),
    queryFn: () => unwrap(api.teams[':teamId'].league.$get({ param: { teamId }, query: { season } })),
    enabled: !!teamId,
  })
export type LeagueTable = Awaited<ReturnType<NonNullable<ReturnType<typeof tableQuery>['queryFn']>>>
export type TableRow = NonNullable<LeagueTable['tables']>['total'][number]
export type FairplayRow = LeagueTable['fairplay'][number]

export const suspensionsQuery = (teamId: string, season: string) =>
  queryOptions({
    queryKey: leagueKeys.suspensions(teamId, season),
    queryFn: () => unwrap(api.teams[':teamId'].league.suspensions.$get({ param: { teamId }, query: { season } })),
    enabled: !!teamId,
  })
export type Suspensions = Awaited<ReturnType<NonNullable<ReturnType<typeof suspensionsQuery>['queryFn']>>>
export type SuspensionRow = Suspensions['suspended'][number]

export const scorersQuery = (teamId: string, season: string) =>
  queryOptions({
    queryKey: leagueKeys.scorers(teamId, season),
    queryFn: () => unwrap(api.teams[':teamId'].league.scorers.$get({ param: { teamId }, query: { season } })),
    enabled: !!teamId,
  })
export type Scorers = Awaited<ReturnType<NonNullable<ReturnType<typeof scorersQuery>['queryFn']>>>
export type ScorerRow = Scorers['items'][number]

export const clubsQuery = (teamId: string, season: string) =>
  queryOptions({
    queryKey: leagueKeys.clubs(teamId, season),
    queryFn: () => unwrap(api.clubs.$get({ query: { teamId, season } })),
    enabled: !!teamId,
  })
export type Clubs = Awaited<ReturnType<NonNullable<ReturnType<typeof clubsQuery>['queryFn']>>>
export type ClubItem = Clubs['items'][number]

export const clubQuery = (teamId: string, season: string, id: string) =>
  queryOptions({
    queryKey: leagueKeys.club(teamId, season, id),
    queryFn: () => unwrap(api.clubs[':id'].$get({ param: { id }, query: { teamId, season } })),
    enabled: !!teamId,
  })
export type ClubDetail = Awaited<ReturnType<NonNullable<ReturnType<typeof clubQuery>['queryFn']>>>

export const personSuspensionsQuery = (personId: string, season: string) =>
  queryOptions({
    queryKey: leagueKeys.person(personId, season),
    queryFn: () => unwrap(api.people[':personId'].suspensions.$get({ param: { personId }, query: { season } })),
  })
export type PersonSuspensions = Awaited<ReturnType<NonNullable<ReturnType<typeof personSuspensionsQuery>['queryFn']>>>
export type PersonBan = PersonSuspensions['items'][number]

type SuspensionCreate = Parameters<typeof api.suspensions.$post>[0]['json']
type SuspensionPatch = Parameters<(typeof api.suspensions)[':id']['$patch']>[0]['json']

/** A ban changes availability too: squad, match day, training, medical and home refetch. */
function useRefresh() {
  const qc = useQueryClient()
  return () => Promise.all([['league'], ['medical'], ['people'], ['matches'], ['training'], ['home']].map((queryKey) => qc.invalidateQueries({ queryKey })))
}

export function useSaveSuspension() {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: (v: { id: string; json: SuspensionPatch } | { json: SuspensionCreate }) =>
      'id' in v ? unwrap(api.suspensions[':id'].$patch({ param: { id: v.id }, json: v.json })) : unwrap(api.suspensions.$post({ json: v.json })),
    onSuccess: refresh,
  })
}

export function useDeleteSuspension() {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: (id: string) => unwrap(api.suspensions[':id'].$delete({ param: { id } })),
    onSuccess: refresh,
  })
}
